import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { deliveriesApi, type Delivery } from "../api/deliveries";
import { procurementContractsApi, type ProcurementContract } from "../api/procurementContracts";
import { card, head, list, itemLink, moreLink, empty } from "./widgetStyles";

/**
 * What the buy-in desk has waiting for it. Chosen by permission
 * (`deliveries:confirm`), not by role, so it survives the configurable
 * permission system when that lands.
 *
 * <p>Five lines, in the order the day runs: announcements to confirm, loads
 * received but not yet analysed, analysed loads waiting to be settled, contracts
 * about to expire with tonnage still owed, and the season's realisation so far.
 *
 * <p>The desk reads every supplier, but its queue is its own: only deliveries
 * and contracts the caller may act on (`canAct`) are counted, the same way the
 * KPIs follow the assignment. An administrator acts on every supplier and so
 * sees the whole desk.
 */
/** The expiry reminder's first warning comes 30 days out; the widget lists a
 * contract from that day on, so a warned contract is always on it. */
const EXPIRY_HORIZON_DAYS = 30;

/**
 * Koliko se aktivnih ugovora čita za ovu tablu. Registar raste sa poslom, pa se traži strana, a kad
 * je strana odsečena tabla to i kaže — broj izračunat nad delom registra koji se predstavlja kao ceo
 * je gori od broja kojeg nema.
 */
const UGOVORA_NA_TABLI = 200;

export function OtkupPendingWidget({ limit = 5 }: { limit?: number }) {
  const { t, i18n } = useTranslation();
  const [announced, setAnnounced] = useState<Delivery[] | null>(null);
  const [received, setReceived] = useState<Delivery[] | null>(null);
  const [contracts, setContracts] = useState<ProcurementContract[] | null>(null);
  const [contractsCut, setContractsCut] = useState(false);

  useEffect(() => {
    const mine = <T extends { canAct: boolean }>(rows: T[]) => rows.filter((r) => r.canAct);
    // Kontrolna tabla pokazuje šta referentu stoji, ne ceo registar: traži se mala strana, a ne sve
    // pa odsecanje u pregledaču. Broj je namerno veći od onoga što se crta, jer se posle čitanja
    // zadržavaju samo redovi na kojima ovaj referent sme da radi.
    void deliveriesApi.list({ status: "ANNOUNCED" }, 0, 50).then((r) => setAnnounced(mine(r.content))).catch(() => setAnnounced([]));
    // A receipt imported from the ERP is settled there and takes no finding here, so it waits for neither.
    void deliveriesApi.list({ status: "RECEIVED", origin: "PORTAL" }, 0, 50).then((r) => setReceived(mine(r.content))).catch(() => setReceived([]));
    void procurementContractsApi.list({ status: "ACTIVE" }, 0, UGOVORA_NA_TABLI)
      .then((r) => {
        setContracts(mine(r.content));
        setContractsCut(r.totalElements > r.content.length);
      })
      .catch(() => setContracts([]));
  }, []);

  if (announced === null || received === null || contracts === null) return null;

  // A received load with no readings is the lab's queue; with readings it is
  // the desk's to settle. The split is the whole point of the widget.
  const awaitingAnalysis = received.filter((d) => d.qualityValues.length === 0);
  const awaitingSettlement = received.filter((d) => d.qualityValues.length > 0);

  // A contract that expires with nothing left to deliver is finished, not
  // urgent — the same rule the expiry reminder uses, so the two agree.
  const horizon = new Date();
  horizon.setDate(horizon.getDate() + EXPIRY_HORIZON_DAYS);
  // Only contracts in force are read here, and the server knows the fulfilment of every one of them;
  // an unknown one is left out of the sum rather than counted as nothing delivered.
  const expiringWithTonnageOwed = contracts.filter(
    (c) => c.expiresAt !== null && new Date(c.expiresAt) <= horizon && c.remainingT != null && c.remainingT > 0,
  );
  const settledT = contracts.reduce((sum, c) => sum + (c.settledT ?? 0), 0);
  const contractedT = contracts.reduce((sum, c) => sum + c.quantityT, 0);

  return (
    <section style={card}>
      <header style={head}>
        <strong>{t("widgets.otkupPending.title", "Waiting for the buy-in desk")}</strong>
        <Link to="/procurement/deliveries" className="widget-more-link" style={moreLink}>
          {t("widgets.otkupPending.viewAll", "All deliveries")}
        </Link>
      </header>
      <ul style={list}>
        <Row
          label={t("widgets.otkupPending.toConfirm", "Announcements awaiting confirmation")}
          count={announced.length}
          to="/procurement/deliveries?status=ANNOUNCED"
        />
        <Row
          label={t("widgets.otkupPending.noAnalysis", "Received, no analysis yet")}
          count={awaitingAnalysis.length}
          to="/procurement/deliveries?status=RECEIVED"
        />
        <Row
          label={t("widgets.otkupPending.toSettle", "Settlements pending")}
          count={awaitingSettlement.length}
          to="/procurement/deliveries?status=RECEIVED"
        />
        <Row
          label={t("widgets.otkupPending.expiring", "Contracts expiring with tonnage owed")}
          count={expiringWithTonnageOwed.length}
          to="/procurement/contracts?status=ACTIVE"
        />
      </ul>

      <p style={{ margin: 0, fontSize: 13, color: "var(--ink)" }}>
        {t("widgets.otkupPending.realisation", "Realised")}:{" "}
        <strong>
          {settledT.toLocaleString(i18n.resolvedLanguage, { maximumFractionDigits: 2 })} t
        </strong>
        {contractedT > 0 && (
          <span style={{ color: "var(--ink-3)" }}>
            {" "}
            / {contractedT.toLocaleString(i18n.resolvedLanguage, { maximumFractionDigits: 2 })} t
          </span>
        )}
      </p>
      {contractsCut && (
        <p style={empty}>
          {t(
            "widgets.otkupPending.contractsCut",
            "Realizacija je računata nad prvih {{n}} aktivnih ugovora.",
            { n: UGOVORA_NA_TABLI },
          )}
        </p>
      )}
      {announced.length + received.length === 0 && (
        <p style={empty}>{t("widgets.otkupPending.empty", "Nothing waiting.")}</p>
      )}
      {announced.length > limit && (
        <p style={empty}>
          {t("widgets.otkupPending.more", "Showing counts only — open the list for detail.")}
        </p>
      )}
    </section>
  );
}

function Row({ label, count, to }: { label: string; count: number; to: string }) {
  return (
    <li>
      <Link to={to} style={itemLink}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
          <span style={{ fontSize: 13, color: "var(--ink)" }}>{label}</span>
          <strong style={{ fontSize: 13, color: count > 0 ? "var(--brand-ink)" : "var(--ink-4)" }}>{count}</strong>
        </div>
      </Link>
    </li>
  );
}
